import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { motion } from 'framer-motion';

const AnimatedParagraph = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: delay / 1000 }}
  >
    {children}
  </motion.div>
);

const AnimatedSection = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: delay / 1000 }}
  >
    {children}
  </motion.div>
);

const UCCPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <SEO
        title="The Uniform Commercial Code (UCC) | Freeman & Crown Law"
        description="Comprehensive guide to understanding commercial law, financing statements, and secured transactions under the Uniform Commercial Code."
        image="/lovable-uploads/15868f15-6c06-4358-98e2-9fc153ff4e2c.png"
        url={window.location.origin + '/ucc'}
        type="website"
      />
      
      <section className="pt-32 pb-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <AnimatedParagraph delay={0}>
            <div className="text-center mb-12">
              <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
                The Uniform Commercial Code
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                Understanding commercial law, financing statements, and secured transactions under the UCC framework.
              </p>
            </div>
          </AnimatedParagraph>

          <div className="prose prose-lg max-w-none">
            <AnimatedSection delay={200}>
              <div className="bg-muted/50 border border-gold/10 rounded-lg p-8 mb-8">
                <h2 className="font-rockwell text-2xl font-semibold text-primary mb-4">
                  What is the Uniform Commercial Code?
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  The Uniform Commercial Code (UCC) is a comprehensive set of laws governing commercial transactions in the United States. 
                  It provides a standardized legal framework for business dealings, including sales of goods, secured transactions, 
                  negotiable instruments, bank deposits, and other commercial matters.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={400}>
              <div className="bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-8 border border-gold/20 mb-8">
                <h2 className="font-rockwell text-2xl font-semibold text-primary mb-6 text-center">
                  Complete Uniform Commercial Code Document
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-6 text-center">
                  Access the full text of the Uniform Commercial Code below. This comprehensive document contains all articles and provisions governing commercial law in the United States.
                </p>
              </div>
            </AnimatedSection>

            {/* Full width iframe section */}
            <AnimatedSection delay={600}>
              <div className="relative -mx-4 md:-mx-8 lg:-mx-16 xl:-mx-24 mb-8">
                <div className="relative w-full h-[80vh] min-h-[600px] px-4 md:px-8 lg:px-16 xl:px-24">
                  <iframe 
                    src="https://archive.org/embed/uniform-commercial-code" 
                    className="w-full h-full rounded-lg border border-gold/30"
                    frameBorder="0" 
                    allowFullScreen
                    title="Uniform Commercial Code"
                  />
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={800}>
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-6 border border-gold/20">
                  <h3 className="font-rockwell text-xl font-semibold text-primary mb-3">
                    Key Articles
                  </h3>
                  <ul className="text-muted-foreground space-y-2">
                    <li>• Article 1: General Provisions</li>
                    <li>• Article 2: Sales of Goods</li>
                    <li>• Article 3: Negotiable Instruments</li>
                    <li>• Article 4: Bank Deposits</li>
                    <li>• Article 9: Secured Transactions</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-6 border border-gold/20">
                  <h3 className="font-rockwell text-xl font-semibold text-primary mb-3">
                    Common Applications
                  </h3>
                  <ul className="text-muted-foreground space-y-2">
                    <li>• Financing statements (UCC-1)</li>
                    <li>• Security interests</li>
                    <li>• Commercial liens</li>
                    <li>• Asset protection</li>
                    <li>• Business transactions</li>
                  </ul>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={1000}>
              <div className="bg-muted/50 border border-gold/10 rounded-lg p-8">
                <h2 className="font-rockwell text-2xl font-semibold text-primary mb-4">
                  Understanding the UCC
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  The UCC provides uniform laws across all states for commercial transactions, making interstate commerce more predictable and efficient. Understanding these provisions is crucial for anyone engaged in business activities, secured lending, or commercial law.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Use the embedded document above to research specific articles, sections, and provisions of the Uniform Commercial Code relevant to your legal and business needs.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UCCPage;